// src/apiAdmin.ts
import axios from "axios";
import { SERVER_URL } from "./config";
import { getAnonId } from "./lib/abTesting";

export const adminApi = axios.create({
  baseURL: `${SERVER_URL}/api/admin`,
  withCredentials: true,
});

const rootApi = axios.create({
  baseURL: `${SERVER_URL}/api`,
  withCredentials: true,
});

// The dashboard tells the server which device it is (the same anonymous id
// the analytics use), so the team's own visits stay out of the Growth
// numbers. Server side: lib/teamDevices.js.
try {
  adminApi.defaults.headers.common["X-Rq-Anon"] = getAnonId();
} catch { /* no-op */ }

let adminAccessToken: string | null = null;

export function setAdminToken(token: string | null) {
  adminAccessToken = token;
  if (token) {
    adminApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete adminApi.defaults.headers.common.Authorization;
  }
}

export function getAdminToken() {
  return adminAccessToken;
}
export function clearAdminToken() {
  setAdminToken(null);
}

try {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem("admin-token");
    if (saved) setAdminToken(saved);
  }
} catch { /* no-op */ }

let csrfInFlight: Promise<void> | null = null;
let csrfToken: string | null = null;

async function fetchCsrfOnce(): Promise<void> {
  const { data } = await rootApi.get("/csrf-token");
  csrfToken = data.csrfToken;
  adminApi.defaults.headers.common["X-CSRF-Token"] = data.csrfToken;
}

export function getAdminCsrf(force = false): Promise<void> {
  if (force) csrfToken = null;
  if (csrfToken) return Promise.resolve();
  if (!csrfInFlight) {
    csrfInFlight = fetchCsrfOnce()
      .catch(() => {})
      .finally(() => {
        csrfInFlight = null;
      });
  }
  return csrfInFlight || Promise.resolve();
}

adminApi.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    await getAdminCsrf();
    // Explicitly attach to THIS request's headers. axios merges defaults at
    // request-build time (before interceptors run), so mutating defaults
    // inside an interceptor doesn't propagate to the current request — only
    // future ones. Without this line, the first state-changing request of
    // a session fails with EBADCSRFTOKEN.
    if (csrfToken) {
      config.headers = config.headers || {};
      (config.headers as Record<string, string>)["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// On stale-CSRF rejection from the server, refetch the token and retry
// once. Belt for the suspenders above — covers cases like server restart
// rotating the secret, or the cookie expiring mid-session.
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config as (typeof error.config & { _csrfRetried?: boolean }) | undefined;
    const isCsrfError =
      error.response?.status === 403 &&
      error.response?.data?.msg === "Invalid CSRF token";
    if (isCsrfError && cfg && !cfg._csrfRetried) {
      cfg._csrfRetried = true;
      await getAdminCsrf(true);
      if (csrfToken) {
        cfg.headers = cfg.headers || {};
        (cfg.headers as Record<string, string>)["X-CSRF-Token"] = csrfToken;
      }
      return adminApi.request(cfg);
    }
    // The server ended this session (signed out everywhere, admin removed,
    // token expired). Drop the dead token and go back to the sign-in page
    // instead of leaving a dashboard where every tab quietly fails. The
    // sign-in calls themselves answer 401 for their own reasons and are left
    // to the page.
    const url = String(cfg?.url || "");
    if (error.response?.status === 401 && !url.startsWith("/login")) {
      endAdminSessionLocally();
    }
    return Promise.reject(error);
  },
);

export function endAdminSessionLocally() {
  clearAdminToken();
  try {
    localStorage.removeItem("admin-token");
    localStorage.removeItem("admin-user");
  } catch { /* no-op */ }
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin/login") {
    // ?next= brings the admin back here after signing in (e.g. a review link
    // from the founder email opened with an expired session).
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/admin/login?next=${encodeURIComponent(next)}`);
  }
}
